import Vue from "vue";

export const ClientState = {
  client: null,
  clients: [],
  totalClients: 0,
  assessments: [],
  clientTargetJob: null,
  clientDocuments: [],
  totalClientDocuments: 0
};

export const ClientGetters = {
  client: state => {
    return state.client;
  },
  clients: state => {
    return state.clients;
  },
  totalClients: state => {
    return state.totalClients;
  },
  assessments: state => {
    return state.assessments;
  },
  clientTargetJob: state => {
    return state.clientTargetJob;
  },
  clientDocuments: state => {
    return state.clientDocuments;
  },
  totalClientDocuments: state => {
    return state.totalClientDocuments;
  }
};

export const ClientMutations = {
  setClient: (state, data) => {
    if (data) {
      const index = state.clients.findIndex(
        i => Number(i.id) === Number(data.id)
      );
      if (index > -1) {
        const arr = [...state.clients];
        arr[index] = data;
        Vue.set(state, "clients", arr);
      }
      Vue.set(state, "totalActionRecommendations", data.total_recommendation);
      Vue.set(state, "clientTargetJob", data.running_create_target_job);
    }
    Vue.set(state, "client", data);
  },
  setClients: (state, data) => {
    Vue.set(state, "clients", data.clients);
    Vue.set(state, "totalClients", data.total_clients);
  },
  addClient: (state, data) => {
    Vue.set(state, "clients", [data, ...state.clients]);
    Vue.set(state, "totalClients", state.totalClients + 1);
  },
  removeClient: (state, data) => {
    const arr = state.clients.filter(i => Number(i.id) !== Number(data.id));
    Vue.set(state, "clients", arr);
    Vue.set(state, "totalClients", state.totalClients - 1);
  },
  //
  setAssessment: (state, data) => {
    if (data) {
      const index = state.assessments.findIndex(
        i => Number(i.id) === Number(data.id)
      );
      if (index > -1) {
        const arr = [...state.assessments];
        arr[index] = data;
        Vue.set(state, "assessments", arr);
      }
    }
  },
  setAssessments: (state, data) => {
    Vue.set(state, "assessments", data);
  },
  addAssessment: (state, data) => {
    Vue.set(state, "assessments", [...state.assessments, data]);
  },
  removeAssessment: (state, data) => {
    const arr = state.assessments.filter(i => Number(i.id) !== Number(data.id));
    Vue.set(state, "assessments", arr);
  },
  //
  setClientTargetJob: (state, data) => {
    Vue.set(state, "clientTargetJob", data);
  },
  setClientDocuments: (state, data) => {
    Vue.set(state, "clientDocuments", data.data);
    Vue.set(state, "totalClientDocuments", data.total_count);
  },
  addClientDocument: (state, data) => {
    Vue.set(state, "clientDocuments", [data, ...state.clientDocuments]);
    Vue.set(state, "totalClientDocuments", state.totalClientDocuments + 1);
  }
};

export const ClientActions = {
  getClients: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    const search = data.search || false;
    return Vue.axios.secured
      .get(`/api/v1/clients${params}`)
      .then(response => {
        if (search) return { success: true, data: response.data.clients };
        commit("setClients", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch clients from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  getClient: ({ commit, dispatch }, data = {}) => {
    if (!data.id) {
      commit("setClient", null);
      return { success: false, data: null };
    }
    return Vue.axios.secured
      .get(`/api/v1/clients/${data.id}`)
      .then(response => {
        commit("setClient", response.data);
        commit("setAssessments", response.data.pre_session_assessments);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch client from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  createClient: ({ commit, dispatch }, data = {}) => {
    const notAdded = data.not_added || false;
    return Vue.axios.secured
      .post("/api/v1/clients", {
        client: data.client,
        label_ids: data.label_ids
      })
      .then(response => {
        if (!notAdded) {
          commit("addClient", { ...response.data, is_new: true });
        }
        const message = `Client has been successfully added.`;
        dispatch("showToast", { message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create client.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateClient: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .patch(`/api/v1/clients/${data.id}`, { client: data })
      .then(response => {
        commit("setClient", response.data);
        const message = `Client has been successfully updated.`;
        dispatch("showToast", { message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update client.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  deleteClient: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .delete(`/api/v1/clients/${data.id}`)
      .then(response => {
        commit("removeClient", data);
        const message = `Client has been successfully removed.`;
        dispatch("showToast", { message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete client.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  //
  createAssessment: ({ commit, dispatch }, data = {}) => {
    const type = data.assessment.type || "";
    return Vue.axios.secured
      .post(`/api/v1/clients/${data.id}/assessments`, {
        assessment: data.assessment
      })
      .then(response => {
        if (type === "Assessment::InSession") {
          commit("addSessionComment", response.data);
        } else {
          commit("addAssessment", response.data);
        }
        const title = `New ABC data added`;
        const message = `Success! The ABC data has been added.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to create assessment.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  updateAssessment: ({ commit, dispatch }, data = {}) => {
    const type = data.assessment.type || "";
    return Vue.axios.secured
      .patch(`/api/v1/clients/${data.id}/assessments/${data.assessment.id}`, {
        assessment: data.assessment
      })
      .then(response => {
        if (type === "Assessment::InSession") {
          commit("setSessionComment", response.data);
        } else {
          commit("setAssessment", response.data);
        }
        const title = `ABC data updated`;
        const message = `Success! The ABC data has been updated.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to update assessment.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  deleteAssessment: ({ commit, dispatch }, data = {}) => {
    const type = data.assessment.type || "";
    return Vue.axios.secured
      .delete(`/api/v1/clients/${data.id}/assessments/${data.assessment.id}`)
      .then(response => {
        if (type === "Assessment::InSession") {
          commit("removeSessionComment", data.assessment);
        } else {
          commit("removeAssessment", data.assessment);
        }
        const title = `ABC data deleted`;
        const message = `Success! The ABC data has been deleted.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete assessment.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  //
  getClientDocuments: ({ commit, dispatch }, data = {}) => {
    const params = data.params || "";
    return Vue.axios.secured
      .get(`/api/v1/clients/${data.client_id}/documents${params}`)
      .then(response => {
        commit("setClientDocuments", response.data);
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to fetch clients from server.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  uploadClientDocument: ({ commit, dispatch }, data = {}) => {
    return Vue.axios.secured
      .post(`/api/v1/clients/${data.client_id}/add_documents`, data.documents)
      .then(response => {
        commit("setClient", response.data);
        commit("setAssessments", response.data.pre_session_assessments);
        // const title = `New document uploaded`;
        // const message = `Success! An document has been added.`;
        // dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to upload document.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  deleteClientDocument: ({ commit, dispatch }, data = {}) => {
    const ids = data.ids.toString();
    return Vue.axios.secured
      .delete(`/api/v1/clients/${data.client_id}/delete_document/${ids}`)
      .then(response => {
        commit("setClient", response.data);
        commit("setAssessments", response.data.pre_session_assessments);
        const title = `Document deleted`;
        const message = `Success! The document has been deleted.`;
        dispatch("showToast", { title, message, type: "success" });
        return { success: true, data: response.data };
      })
      .catch(error => {
        const err = error.response && error.response.data;
        const message = err || "Unable to delete document.";
        dispatch("showToast", { message, type: "error" });
        return { success: false, data: null };
      });
  },
  downloadClientDocument: ({ dispatch }, data = {}) => {
    const ids = data.ids.toString();
    return Vue.axios.secured
      .get(
        `/api/v1/clients/${data.client_id}/download_files?document_id=${ids}`,
        { responseType: "blob" }
      )
      .catch(error => {
        console.error(error);
        // const err = error.response && error.response.data;
        // const message = err || "Unable to download report.";
        // dispatch("showToast", { message, type: "error" });
      });
  }
};



// WEBPACK FOOTER //
// ./src/store/modules/client.store.js